import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Backdrop,
  Box,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import {
  Add as AddIcon,
  Style as StyleIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  KeyboardCommandKey as CmdIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import CommandPalette from './CommandPalette';
import QuickChatDrawer from './QuickChatDrawer';
import ShortcutsDialog from './ShortcutsDialog';
import { useUI } from '../contexts/UIContext';

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

const FloatingActions: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useUI();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  // Listen for command palette "Keyboard Shortcuts" command
  useEffect(() => {
    const handler = () => setShortcutsOpen(true);
    window.addEventListener('wardrobe:open-shortcuts', handler);
    return () => window.removeEventListener('wardrobe:open-shortcuts', handler);
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTextInput =
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      // Cmd/Ctrl + K → command palette (always works, even in inputs)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((p) => !p);
        return;
      }

      // Cmd/Ctrl + J → quick chat
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setChatOpen((p) => !p);
        return;
      }

      // Single-key shortcuts only when not typing
      if (!isTextInput && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        if (e.key === '/') {
          e.preventDefault();
          setPaletteOpen(true);
        } else if (e.key === '?') {
          e.preventDefault();
          setShortcutsOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toast]);

  // Two-key sequences like "g w" → wardrobe
  useEffect(() => {
    let pendingG = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTextInput =
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTextInput || e.metaKey || e.ctrlKey || e.altKey) return;

      if (pendingG) {
        const map: Record<string, string> = {
          d: '/',
          w: '/wardrobe',
          u: '/upload',
          o: '/outfits',
          c: '/conversations',
          s: '/search',
          r: '/recommendations',
          a: '/activity',
          p: '/profile',
        };
        const dest = map[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
        pendingG = false;
        if (timeout) clearTimeout(timeout);
        return;
      }

      if (e.key.toLowerCase() === 'g') {
        pendingG = true;
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => { pendingG = false; }, 900);
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (timeout) clearTimeout(timeout);
    };
  }, [navigate]);

  // Global drag-and-drop for files
  useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
    // Screens with their own drop target (Look Book, item photo gallery) mark it
    // with data-local-dropzone; the global "add to wardrobe" overlay stands down there.
    const localDropzoneOnScreen = () => !!document.querySelector('[data-local-dropzone]');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e) || localDropzoneOnScreen()) return;
      dragCounter.current += 1;
      setDragActive(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setDragActive(false);
    };
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounter.current = 0;
      setDragActive(false);
      if (localDropzoneOnScreen()) {
        // The local dropzone handles drops on itself; anything else is just ignored
        // (default prevented so the browser doesn't open the file).
        const target = e.target as Element | null;
        if (!target?.closest?.('[data-local-dropzone]')) toast('Drop photos onto the upload area', 'info');
        return;
      }
      const files = Array.from(e.dataTransfer?.files || []);
      const images = files.filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) {
        toast('Drop image files to add wardrobe items', 'warning');
        return;
      }
      try {
        // Stash files in a transfer object for the Upload page to consume
        (window as any).__pendingUploadFiles = images;
        navigate('/upload?source=drop');
        toast(`Sending ${images.length} image${images.length > 1 ? 's' : ''} to Upload…`, 'success');
      } catch {
        toast('Could not start upload', 'error');
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [navigate, toast]);

  const speedActions = [
    { name: 'Add Item', icon: <AddIcon />, onClick: () => navigate('/upload') },
    { name: 'Create Outfit', icon: <StyleIcon />, onClick: () => navigate('/outfits?action=create') },
    { name: 'AI Stylist', icon: <ChatIcon />, onClick: () => setChatOpen(true) },
    { name: 'Search', icon: <SearchIcon />, onClick: () => setPaletteOpen(true) },
  ];

  const cmdLabel = isMac ? '⌘K' : 'Ctrl+K';

  return (
    <>
      {/* Drag-and-drop overlay */}
      <Backdrop
        open={dragActive}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(26,26,26,0.85)',
          flexDirection: 'column',
          color: '#fff',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box
          sx={{
            border: '2px dashed rgba(212, 175, 55, 0.85)',
            px: 6,
            py: 5,
            textAlign: 'center',
            maxWidth: 420,
            animation: 'pulse 1.6s ease-in-out infinite',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 64, color: 'secondary.main', mb: 1.5 }} />
          <Typography variant="h5" sx={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.08em' }}>
            Drop to add to wardrobe
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
            We'll detect items and let you confirm before saving
          </Typography>
        </Box>
      </Backdrop>

      {/* Cmd+K hint chip (subtle, above FAB) */}
      <Zoom in={!speedOpen}>
        <Tooltip title="Open command palette" placement="left">
          <Box
            onClick={() => setPaletteOpen(true)}
            sx={{
              position: 'fixed',
              right: 24,
              bottom: 168,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              backgroundColor: 'background.paper',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              zIndex: (theme) => theme.zIndex.speedDial - 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'secondary.main',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CmdIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>
              {cmdLabel}
            </Typography>
          </Box>
        </Tooltip>
      </Zoom>

      {/* Floating chat launcher */}
      <Tooltip title="AI Stylist (⌘J)" placement="left">
        <Fab
          color="primary"
          onClick={() => setChatOpen(true)}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 96,
            // Redundant with the mobile bottom "Assistant" tab — hide on mobile
            display: { xs: 'none', md: 'inline-flex' },
            zIndex: (theme) => theme.zIndex.speedDial,
            backgroundColor: 'background.paper',
            color: 'primary.main',
            border: '2px solid',
            borderColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .ai-spark': { color: 'secondary.light' },
            },
          }}
        >
          <AutoAwesomeIcon className="ai-spark" sx={{ color: 'secondary.main' }} />
        </Fab>
      </Tooltip>

      {/* SpeedDial for quick actions */}
      <SpeedDial
        ariaLabel="Quick actions"
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 24 },
          // Sit above the mobile bottom tab bar (+ safe area); normal on desktop
          bottom: { xs: 'calc(72px + env(safe-area-inset-bottom))', md: 24 },
        }}
        icon={<SpeedDialIcon />}
        open={speedOpen}
        onOpen={() => setSpeedOpen(true)}
        onClose={() => setSpeedOpen(false)}
        FabProps={{ color: 'primary' }}
      >
        {speedActions.map((a) => (
          <SpeedDialAction
            key={a.name}
            icon={a.icon}
            tooltipTitle={a.name}
            tooltipOpen
            onClick={() => {
              setSpeedOpen(false);
              a.onClick();
            }}
            sx={{
              '& .MuiSpeedDialAction-staticTooltipLabel': {
                whiteSpace: 'nowrap',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 500,
                letterSpacing: '0.04em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
              },
            }}
          />
        ))}
      </SpeedDial>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <QuickChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </>
  );
};

export default FloatingActions;
